import React, { useState, useRef, useEffect } from 'react';
import ReactGA from 'react-ga4';
import { Tooltip } from '../Tooltip';
import { convertUrlToLanguage } from '../../utils/algolia/url-handling';
import { normalizeSupportedLocale } from '../../utils/routing/locale-from-pathname';
import type { IntlShape } from 'react-intl';

export interface LanguageSelectorProps {
  intl: IntlShape;
  /** Current locale; if missing or invalid, falls back to intl.locale so the button is never empty */
  locale?: 'en' | 'fi' | 'sv';
  className?: string;
}

/**
 * Language selector component for navigation
 * Shows current language flag and dropdown to switch languages
 * Only visible on xl screens and above
 */
export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  intl,
  locale: localeProp,
  className = '',
}) => {
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const languageMenuRef = useRef<HTMLDivElement>(null);
  // Ensure we always have a valid display locale (static SSR may pass undefined; hydration may pass wrong value)
  const locale = normalizeSupportedLocale(localeProp ?? intl?.locale);

  // Close language menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (languageMenuRef.current && !languageMenuRef.current.contains(event.target as Node)) {
        setIsLanguageMenuOpen(false);
      }
    };
    if (isLanguageMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isLanguageMenuOpen]);

  const handleLanguageChange = (newLocale: 'en' | 'fi' | 'sv') => {
    ReactGA.event({
      category: 'interactions',
      action: 'click_change_language',
      label: newLocale,
      nonInteraction: false,
    });
    window.location.href = convertUrlToLanguage(window.location.pathname, newLocale, intl);
  };

  return (
    <div ref={languageMenuRef} className={`hidden xl:block relative cursor-pointer ${className}`}>
      <Tooltip
        content={intl.formatMessage({
          id: 'tooltip.language',
          defaultMessage: 'Change website language (English, Finnish, Swedish)',
          description: 'Tooltip text for language selector button',
        })}
        position="bottom"
      >
        <button
          onClick={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-300 bg-white/60 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          aria-label={intl.formatMessage({
            id: 'navigation.language',
            defaultMessage: 'Language',
            description: 'Button aria-label for language selector in navigation',
          })}
        >
          {locale === 'fi' && <span className="text-xs">FI</span>}
          {locale === 'sv' && <span className="text-xs">SV</span>}
          {locale === 'en' && <span className="text-xs">EN</span>}
        </button>
      </Tooltip>
      {isLanguageMenuOpen && (
        <div className="absolute top-full right-0 mt-2 w-40 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 z-50">
          <button
            onClick={() => handleLanguageChange('fi')}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
              locale === 'fi'
                ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <span>FI</span>
          </button>
          <button
            onClick={() => handleLanguageChange('sv')}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
              locale === 'sv'
                ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <span>SV</span>
          </button>
          <button
            onClick={() => handleLanguageChange('en')}
            className={`w-full flex items-center gap-2 px-4 py-2 text-sm transition-colors cursor-pointer ${
              locale === 'en'
                ? 'bg-green-100 text-green-900 dark:bg-green-200 dark:text-black font-medium'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            <span>EN</span>
          </button>
        </div>
      )}
    </div>
  );
};
